import React, { useEffect, useState } from 'react';
import './MyCertificates.css';
import { useNavigate } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SchoolIcon from '@mui/icons-material/School';
import { Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import { getMyCertificates, downloadCertificatePdf, getAllCertificationsAuth } from '../../../api/certification';

const BADGE_COLORS = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#D4AF37',
  platinum: '#6E7F80',
};

function getBadgeColor(level) {
  return BADGE_COLORS[String(level || '').toLowerCase()] || '#4be3cf';
}

function getBadgeIconPath(badgeType) {
  const type = String(badgeType || '').toLowerCase();
  if (type === 'bronze') return '/Assets/Certification/bronze.svg';
  if (type === 'silver') return '/Assets/Certification/silver.svg';
  if (type === 'gold') return '/Assets/Certification/gold.svg';
  if (type === 'platinum') return '/Assets/Certification/platinum.svg';
  return '/Assets/Certification/bronze.svg';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export default function MyCertificates() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [allCertifications, setAllCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        let certsList = [];
        let allCertsList = [];

        try {
          const res = await getMyCertificates();
          certsList = res?.data ?? res ?? [];
        } catch (err) {
          console.error("Error fetching my certificates:", err);
        }

        try {
          const res = await getAllCertificationsAuth();
          allCertsList = res?.data ?? res ?? [];
        } catch (err) {
          console.error("Error fetching all certifications:", err);
        }

        setCertificates(Array.isArray(certsList) ? certsList : []);
        setAllCertifications(Array.isArray(allCertsList) ? allCertsList : []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else showSnackbar(e?.response?.data?.message || 'Failed to load certification data.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [navigate]);

  const handleDownload = async (cert) => {
    const id = cert.userCertificateId ?? cert.id;
    const filename = `${cert.certificateNumber ?? cert.certNumber ?? id}.pdf`;
    setDownloadingId(id);
    try {
      const blob = await downloadCertificatePdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSnackbar('Certificate downloaded!', 'success');
    } catch (e) {
      showSnackbar(e?.message || 'Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Loading skeletons ──
  if (loading) {
    return (
      <div className="mc-page">
        <div className="mc-container">
          <div className="mc-header">
            <div className="mc-header-icon-wrap">
              <WorkspacePremiumIcon className="mc-header-icon" />
            </div>
            <div>
              <h1 className="mc-title">My Certificates</h1>
              <p className="mc-subtitle">Your earned certifications</p>
            </div>
          </div>

          {/* Stats Grid Skeletons */}
          <div className="mc-stats-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="mc-stat-card mc-stat-card-skeleton">
                <div className="mc-skeleton mc-stat-skel-icon" />
                <div className="mc-stat-skel-content">
                  <div className="mc-skeleton mc-stat-skel-value" />
                  <div className="mc-skeleton mc-stat-skel-label" />
                </div>
              </div>
            ))}
          </div>

          <div className="mc-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="mc-card mc-card-skeleton">
                <div className="mc-skeleton mc-skel-badge" />
                <div className="mc-skel-body">
                  <div className="mc-skeleton mc-skel-title" />
                  <div className="mc-skeleton mc-skel-meta" />
                  <div className="mc-skeleton mc-skel-btn" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Compute Stats
  const activeEarned = certificates.filter(c => !c.isExpired).length;
  const totalEarned = certificates.length;
  
  const inProgressCount = allCertifications.filter(
    c => c.userEnrollmentStatus === 'pending' || c.userEnrollmentStatus === 'in_progress'
  ).length;

  const availableCount = allCertifications.filter(
    c => !c.userEnrollmentStatus
  ).length;

  return (
    <div className="mc-page">
      <div className="mc-container">
        {/* Header */}
        <div className="mc-header">
          <div className="mc-header-icon-wrap">
            <WorkspacePremiumIcon className="mc-header-icon" />
          </div>
          <div>
            <h1 className="mc-title">My Certificates</h1>
            <p className="mc-subtitle">Your earned certifications</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mc-stats-grid">
          {/* Card 1: Earned */}
          <motion.div
            className="mc-stat-card mc-stat-card-earned"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
          >
            <div className="mc-stat-icon-wrap theme-emerald">
              <WorkspacePremiumIcon className="mc-stat-icon" />
            </div>
            <div className="mc-stat-info">
              <h2 className="mc-stat-value">
                {totalEarned}
              </h2>
              <p className="mc-stat-label">Earned Certificates</p>
            </div>
          </motion.div>

          {/* Card 2: In Progress */}
          <motion.div
            className="mc-stat-card mc-stat-card-progress"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            onClick={() => navigate('/certifications')}
          >
            <div className="mc-stat-icon-wrap theme-amber">
              <AutorenewIcon className="mc-stat-icon" />
            </div>
            <div className="mc-stat-info">
              <h2 className="mc-stat-value">{inProgressCount}</h2>
              <p className="mc-stat-label">In Progress</p>
            </div>
          </motion.div>

          {/* Card 3: Available */}
          <motion.div
            className="mc-stat-card mc-stat-card-available"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.15 }}
            onClick={() => navigate('/certifications')}
          >
            <div className="mc-stat-icon-wrap theme-indigo">
              <SchoolIcon className="mc-stat-icon" />
            </div>
            <div className="mc-stat-info">
              <h2 className="mc-stat-value">{availableCount}</h2>
              <p className="mc-stat-label">Available Courses</p>
            </div>
          </motion.div>
        </div>

        {/* Empty state */}
        {certificates.length === 0 && (
          <div className="mc-empty">
            <WorkspacePremiumIcon style={{ fontSize: 52, color: '#3a3f4a', marginBottom: 12 }} />
            <p className="mc-empty-title">No certificates yet</p>
            <p className="mc-empty-sub">Complete a certification exam to earn your first certificate.</p>
            <button className="mc-btn-browse" onClick={() => navigate('/certifications')}>
              Browse Certifications
            </button>
          </div>
        )}

        {/* Certificate list */}
        <div className="mc-list">
          {certificates.map((cert, idx) => {
            const certId = cert.userCertificateId ?? cert.id;
            const badgeColor = getBadgeColor(cert.badgeType ?? cert.badgeLevel ?? cert.level);
            const isExpired = cert.isExpired ?? false;
            const isDownloading = downloadingId === certId;

            return (
              <motion.div
                key={certId}
                className={`mc-card${isExpired ? ' mc-card-expired' : ''}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.05 }}
                onClick={() => navigate(`/my-certificates/${certId}`)}
              >
                {/* Badge icon */}
                <div
                  className="mc-badge-icon"
                  style={{ background: `${badgeColor}1a`, border: `2px solid ${badgeColor}44` }}
                >
                  <img
                    src={getBadgeIconPath(cert.badgeType ?? cert.badgeLevel ?? cert.level)}
                    alt={`${cert.badgeType ?? 'Certificate'} Badge`}
                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                  />
                </div>

                {/* Body */}
                <div className="mc-card-body">
                  <div className="mc-card-top-row">
                    <h3 className="mc-cert-name">{cert.certificationName ?? cert.name}</h3>
                    {isExpired ? (
                      <span className="mc-expired-badge">Expired</span>
                    ) : (
                      <span className="mc-active-badge">Active</span>
                    )}
                  </div>

                  {cert.certificateNumber && (
                    <p className="mc-cert-number">#{cert.certificateNumber}</p>
                  )}

                  <div className="mc-meta-row">
                    <span className="mc-meta-chip mc-meta-score">
                      Score: {cert.score ?? '—'}%
                    </span>
                    <span 
                      className="mc-meta-chip" 
                      style={{ 
                        color: badgeColor, 
                        borderColor: `${badgeColor}33`, 
                        border: '1px solid', 
                        background: `${badgeColor}12`,
                        fontWeight: 600
                      }}
                    >
                      {String(cert.badgeType ?? cert.badgeLevel ?? cert.level ?? 'bronze').charAt(0).toUpperCase() + String(cert.badgeType ?? cert.badgeLevel ?? cert.level ?? 'bronze').slice(1)} Badge
                    </span>
                    <span className="mc-meta-chip">
                      <CalendarTodayIcon style={{ fontSize: 12 }} />
                      Issued: {formatDate(cert.issuedOn ?? cert.issuedAt ?? cert.issueDate)}
                    </span>
                    {(cert.expiresOn ?? cert.expiresAt ?? cert.expiryDate) && (
                      <span className={`mc-meta-chip ${isExpired ? 'mc-meta-expired' : ''}`}>
                        <CalendarTodayIcon style={{ fontSize: 12 }} />
                        Expires: {formatDate(cert.expiresOn ?? cert.expiresAt ?? cert.expiryDate)}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mc-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="mc-btn-view"
                      onClick={() => navigate(`/my-certificates/${certId}`)}
                    >
                      View Details
                    </button>
                    <button
                      className="mc-btn-download"
                      disabled={isDownloading}
                      onClick={(e) => { e.stopPropagation(); handleDownload(cert); }}
                    >
                      <DownloadIcon style={{ fontSize: 16 }} />
                      {isDownloading ? 'Downloading…' : 'Download'}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
